/**
 * Wd adjustments service (wd-adjustments.service.ts).
 *
 * @version
 * 4.71.0 OAUI-684 Improve shared code and add interfaces
 * 4.63.0 DEVOPS-715 order-admin-ui: setup new repo without vertical slicing
 * 4.62.0 OAUI-654 Remove calls to getOrderCrudApiRouting
 * 4.61.0 OAUI-643 Add material and gauge fields for adjustments
 * 4.55.0 OAUI-619 Updating and deleting adjustments not calling endpoints correctly
 * 4.47.0 OAUI-578 Add a fulfillment groups drop-down on the adjustments
 * 4.37.0 OAUI-527 Slowdowns in order modals
 * 4.36.0 OAUI-519 Add / Edit Adjustments
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @copyright (c) 2022-2024 Build Centrix
 */
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { WdLocalSharedFunctionsService } from '../../shared/wd-local-shared-functions.service';

@Injectable({
  providedIn: 'root',
})
export class WdOrderAdjustmentsService {
  // Behaviour Subjects
  isAddingOrderAdjustments = new BehaviorSubject(false);
  isDeletingOrderAdjustments = new BehaviorSubject(false);
  isUpdatingOrderAdjustments = new BehaviorSubject(false);

  /**
   * Constructor
   * @since 4.36.0
   */
  constructor(
    private wdLocalSharedFunctions: WdLocalSharedFunctionsService,
    private wdTranslate: TranslateService,
    @Inject('wdErrorService') public wdErrorService: any,
    @Inject('wdOauth2HttpService') public wdOauth2HttpService: any,
  ) {}

  /**
   * Build Adjustment Post Data
   * @since 4.47.0
   */
  static buildAdjustmentData(orderIndex: string, data: any) {
    return {
      orderIndex: orderIndex,
      fulfillmentGroupIndex: data.group.id,
      label: data.label,
      description: data.description,
      costAdjustment:
        data.cost && data.cost.value ? parseFloat(data.cost.value) : null,
      gauge: data.gauge && data.gauge.value ? data.gauge.value : null,
      materialId: data.material && data.material.id ? data.material.id : null,
      priceAdjustment:
        data.price && data.price.value ? parseFloat(data.price.value) : null,
      shopLabourAdjustment:
        data.shopLabour && data.shopLabour.value
          ? parseFloat(data.shopLabour.value)
          : null,
      weightAdjustment:
        data.weight && data.weight.value ? parseFloat(data.weight.value) : null,
    };
  }

  /**
   * Add Order Adjustment
   * @since 4.47.0
   */
  async addOrderAdjustment(orderIndex: string, inputData: any): Promise<void> {
    // Toggle Loader
    this.isAddingOrderAdjustments.next(true);
    try {
      // Format data for POST
      const postData = WdOrderAdjustmentsService.buildAdjustmentData(
        orderIndex,
        inputData,
      );
      // Add Order Adjustment
      await this.wdOauth2HttpService.post(
        `${this.wdLocalSharedFunctions.publicApiUrl}orders/adjustments?responseFormat=bcxResult`,
        postData,
      );
      // Success Message
      this.wdErrorService.userSuccess(
        this.wdTranslate.instant('labels.success'),
        this.wdTranslate.instant('outputs.successfullyAdded', {
          type: this.wdTranslate.instant('order.adjustment.singular.title'),
        }),
      );
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        error.statusText,
      );
      throw error;
    } finally {
      // Toggle Loader
      this.isAddingOrderAdjustments.next(false);
    }
  }

  /**
   * Delete Order Adjustment
   * @since 4.55.0
   */
  async deleteOrderAdjustment(
    orderIndex: string,
    adjustmentId: string,
  ): Promise<void> {
    // Toggle Loader
    this.isDeletingOrderAdjustments.next(true);
    try {
      // Delete Order Adjustment
      await this.wdOauth2HttpService.delete(
        `${this.wdLocalSharedFunctions.publicApiUrl}orders/adjustments/${adjustmentId}?responseFormat=bcxResult&orderIndex=${orderIndex}`,
      );
      // Success Message
      this.wdErrorService.userSuccess(
        this.wdTranslate.instant('labels.success'),
        this.wdTranslate.instant('outputs.successfullyDeleted', {
          type: this.wdTranslate.instant('order.adjustment.singular.title'),
        }),
      );
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        error.statusText,
      );
      throw error;
    } finally {
      // Toggle Loader
      this.isDeletingOrderAdjustments.next(false);
    }
  }

  /**
   * Edit Order Adjustment
   * @since 4.47.0
   */
  async editOrderAdjustment(orderIndex: string, inputData: any): Promise<void> {
    // Toggle Loader
    this.isUpdatingOrderAdjustments.next(true);
    try {
      // Format data for PUT
      const putData = WdOrderAdjustmentsService.buildAdjustmentData(
        orderIndex,
        inputData,
      );
      // if the adjustment is added via core-api then it would have an integer index,
      // if the adjustment is added via order-api then it would have a 24 character Object ID
      const id = inputData.id ? inputData.id : inputData.index;
      // Update Order Adjustment
      await this.wdOauth2HttpService.put(
        `${this.wdLocalSharedFunctions.publicApiUrl}orders/adjustments/${id}?responseFormat=bcxResult`,
        putData,
      );
      // Success Message
      this.wdErrorService.userSuccess(
        this.wdTranslate.instant('labels.success'),
        this.wdTranslate.instant('outputs.successfullyUpdated', {
          type: this.wdTranslate.instant('order.adjustment.singular.title'),
        }),
      );
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        error.statusText,
      );
      throw error;
    } finally {
      // Toggle Loader
      this.isUpdatingOrderAdjustments.next(false);
    }
  }
}
